// src/components/dashboard/EarningsTab.tsx
// ============================================================================
// KULLANICI KAZANÇ TAB'I
// ============================================================================
// Bu component PaymentsSection içinde bir tab olarak kullanılır.
// Kullanıcının kazanç dönemlerini ve ödeme durumlarını gösterir.
// Doğrulanmış/Doğrulanmamış sipariş ayrımı yapar.
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle,
  Calendar,
  ChevronRight,
  ChevronDown,
  Package,
  AlertCircle,
  AlertTriangle,
  X,
  Loader2,
  ArrowRightCircle,
} from 'lucide-react';
import {
  getUserEarnings,
  getPeriodDetail,
  UserEarningsResponse,
  EarningPeriod,
  PeriodOrder,
  formatCurrency,
  formatDate,
  formatShortDate,
} from '../../services/earnings-api';

// Status Icon Component
const StatusIcon: React.FC<{ status: EarningPeriod['status'] }> = ({ status }) => {
  switch (status) {
    case 'Paid':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'PaymentPending':
      return <Clock className="w-5 h-5 text-amber-500" />;
    case 'AwaitingPayment':
      return <Calendar className="w-5 h-5 text-purple-500" />;
    case 'InProgress':
      return <TrendingUp className="w-5 h-5 text-blue-500" />;
    case 'CarriedOver':
      return <ArrowRightCircle className="w-5 h-5 text-sky-500" />;
    default:
      return <AlertCircle className="w-5 h-5 text-gray-500" />;
  }
};

/**
 * Devir bilgisi rozeti — kullanıcı parasının kaybolmadığını, sadece ertelendiğini görsün.
 * Hem "bu dönem devredildi" hem "bu döneme şuralardan geldi" durumunu gösterir.
 */
const CarryOverNote: React.FC<{ period: EarningPeriod; minPayout?: number }> = ({ period, minPayout }) => {
  const carryIns = period.carryIns ?? [];
  const isCarriedOut = period.status === 'CarriedOver' && !!period.carriedToPeriodName;

  if (!isCarriedOut && carryIns.length === 0) return null;

  return (
    <div className="mt-2 p-3 bg-sky-50 border border-sky-200 rounded-xl">
      {isCarriedOut && (
        <p className="text-sm text-sky-800 flex items-start gap-2">
          <ArrowRightCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {/* Devrin SEBEBİ önce yazılır — kullanıcı neden ödenmediğini bilmeli */}
            {minPayout != null && (
              <>
                Bu dönemin tutarı asgari ödeme tutarı olan <strong>{formatCurrency(minPayout)}</strong>'ın
                altında kaldığı için{' '}
              </>
            )}
            {minPayout == null && <>Bu dönemin tutarı </>}
            <strong>{period.carriedToPeriodName}</strong> dönemine devredildi.
            Kazancınız kaybolmadı — o dönemin ödemesiyle birlikte yatırılacak.
          </span>
        </p>
      )}

      {carryIns.length > 0 && (
        <div className={isCarriedOut ? 'mt-2 pt-2 border-t border-sky-200' : ''}>
          <p className="text-sm font-medium text-sky-800 mb-1">
            Bu döneme devirle gelen tutar: {formatCurrency(period.carryInAmount ?? 0)}
          </p>
          <div className="space-y-0.5">
            {carryIns.map((c) => (
              <div key={`${c.year}-${c.month}`} className="flex justify-between text-xs text-sky-700">
                <span>{c.periodName} döneminden</span>
                <span className="font-medium">{formatCurrency(c.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Period Detail Modal
interface PeriodDetailModalProps {
  period: EarningPeriod;
  onClose: () => void;
}

const PeriodDetailModal: React.FC<PeriodDetailModalProps> = ({ period, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<PeriodOrder[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        setLoading(true);
        const data = await getPeriodDetail(period.year, period.month);
        setOrders(data.orders);
      } catch (err) {
        setError('Dönem detayları yüklenemedi');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [period.year, period.month]);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-xl w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b p-4 flex items-center justify-between bg-gradient-to-r from-orange-50 to-white">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{period.periodName}</h3>
            <p className="text-sm text-gray-500">{period.deliveredOrderCount} teslim edilmiş sipariş</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(80vh-100px)]">
          {/* Kazanç Özeti */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-green-50 rounded-xl">
              <p className="text-sm text-gray-600">✅ Doğrulanmış</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(period.verifiedEarnings)}</p>
              <p className="text-xs text-gray-500">{period.verifiedOrderCount} sipariş</p>
            </div>
            {period.unverifiedEarnings > 0 && (
              <div className="p-3 bg-red-50 rounded-xl">
                <p className="text-sm text-gray-600">⚠️ Doğrulanmamış</p>
                <p className="text-xl font-bold text-red-500">{formatCurrency(period.unverifiedEarnings)}</p>
                <p className="text-xs text-gray-500">{period.unverifiedOrderCount} sipariş</p>
              </div>
            )}
          </div>

          {/* Status */}
          <div
            className="p-3 rounded-xl border"
            style={{
              borderColor: period.statusColor,
              backgroundColor: `${period.statusColor}10`,
            }}
          >
            <div className="flex items-center gap-2">
              <StatusIcon status={period.status} />
              <span className="font-medium" style={{ color: period.statusColor }}>
                {period.statusDescription}
              </span>
            </div>
            {period.status !== 'Paid' && (
              <p className="text-sm text-gray-600 mt-1">
                Ödeme Penceresi: {period.paymentWindowText}
              </p>
            )}
            {period.status === 'Paid' && period.paidAtUtc && (
              <p className="text-sm text-green-600 mt-1">
                ✓ {formatDate(period.paidAtUtc)} tarihinde ödendi
                {period.transactionReference && ` • Ref: ${period.transactionReference}`}
              </p>
            )}
          </div>

          {/* Doğrulanmamış Uyarı */}
          {period.hasUnverifiedOrders && period.status !== 'Paid' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-800">Doğrulanmamış Siparişler</p>
                  <p className="text-xs text-red-700 mt-1">
                    {period.unverifiedOrderCount} sipariş ödeme tarihinden önce güncellenmediği için doğrulanmamış. 
                    Chrome eklentisini çalıştırarak siparişlerinizi güncelleyin.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Orders */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Siparişler
            </h4>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
              </div>
            ) : error ? (
              <div className="text-center py-4 text-red-500">{error}</div>
            ) : (
              <div className="space-y-2">
                {orders.map((order) => (
                  <div 
                    key={order.id} 
                    className={`p-3 rounded-lg flex items-center justify-between ${
                      order.isVerified 
                        ? 'bg-gray-50' 
                        : 'bg-red-50 border border-red-200'
                    }`}
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800">
                        #{order.orderId.slice(-10)}
                        {!order.isVerified && <span className="text-red-500 ml-1">⚠️</span>}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatShortDate(order.orderDateUtc)} • {order.itemCount} ürün
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{formatCurrency(order.total)}</p>
                      <p className={`text-sm font-semibold ${order.isVerified ? 'text-green-600' : 'text-red-500'}`}>
                        +{formatCurrency(order.earnings)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const EarningsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<UserEarningsResponse | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<EarningPeriod | null>(null);
  const [expandedPeriod, setExpandedPeriod] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getUserEarnings();
        setData(result);
      } catch (err) {
        setError('Kazanç bilgileri yüklenemedi');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-600">{error || 'Bir hata oluştu'}</p>
      </div>
    );
  }

  const { summary, periods } = data;

  return (
    <div className="space-y-6">
      {/* Doğrulama Nedir? - kartların üstünde */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-900">Doğrulama Nedir?</p>
          <p className="text-sm text-blue-700">
            Bir siparişin ödenebilmesi için, teslim edildikten yaklaşık 30 gün sonra (Amazon iade süresi dolduktan sonra) Chrome eklentiniz tarafından en az bir kez güncellenmiş olması gerekir.
            Bu, siparişin iptal veya iade edilmediğini doğrular. Eklentinizi her ay çalıştırarak tüm siparişlerinizin doğrulanmasını sağlayabilirsiniz.
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Toplam Kazanç</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(summary.totalAllTimeEarnings)}
              </p>
            </div>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Ödenen</p>
              <p className="text-2xl font-bold text-gray-800">
                {formatCurrency(summary.totalPaidEarnings)}
              </p>
            </div>
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Bekleyen ✅</p>
              <p className="text-2xl font-bold text-amber-600">
                {formatCurrency(summary.totalPendingEarnings)}
              </p>
              {/* Devredilen tutar bu toplama DAHİL — kullanıcı parasının kaybolmadığını görsün */}
              {(summary.totalCarriedOverEarnings ?? 0) > 0 && (
                <p className="text-xs text-sky-600 mt-1">
                  {formatCurrency(summary.totalCarriedOverEarnings!)} sonraki döneme devredildi
                  {summary.minPayoutAmount != null && (
                    <> (asgari {formatCurrency(summary.minPayoutAmount)} altı)</>
                  )}
                </p>
              )}
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Doğrulanmamış ⚠️</p>
              <p className="text-2xl font-bold text-red-500">
                {formatCurrency(summary.totalUnverifiedEarnings)}
              </p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Doğrulanmamış Siparişler Uyarısı */}
      {summary.totalUnverifiedEarnings > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-r-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-red-800">Doğrulanmamış Siparişler</p>
              <p className="text-sm text-red-700 mt-1">
                {formatCurrency(summary.totalUnverifiedEarnings)} tutarındaki siparişleriniz uzun süredir güncellenmedi. 
                Ödeme alabilmek için <strong>Chrome eklentisini çalıştırarak</strong> siparişlerinizi güncelleyin.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Next Payment Info */}
      {summary.nextPaymentDate && (
        <div className="bg-orange-50 border-l-4 border-orange-500 rounded-r-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-orange-600" />
              <div>
                <p className="text-sm text-gray-600">Sonraki Ödeme (Doğrulanmış)</p>
                <p className="font-semibold text-gray-800">
                  {formatDate(summary.nextPaymentDate)} tarihinde{' '}
                  {formatCurrency(summary.nextPaymentAmount || 0)}
                </p>
              </div>
            </div>
            <div className="text-right text-xs text-gray-500">
              <p>Amazon Associate kuralları gereği</p>
              <p>dönem bitiminden 2 ay sonra</p>
            </div>
          </div>
        </div>
      )}

      {/* Periods List */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="border-b border-gray-200 p-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-gray-800">Kazanç Dönemleri</h3>
          <span className="text-sm text-gray-500 ml-auto">{periods.length} dönem</span>
        </div>

        <div className="divide-y divide-gray-100">
          {periods.map((period) => {
            const periodKey = `${period.year}-${period.month}`;
            const isExpanded = expandedPeriod === periodKey;

            return (
              <div key={periodKey}>
                <div
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setExpandedPeriod(isExpanded ? null : periodKey)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <StatusIcon status={period.status} />
                      <div>
                        <p className="font-medium text-gray-800">{period.periodName}</p>
                        <p className="text-sm text-gray-500">
                          {period.deliveredOrderCount} sipariş
                          {period.hasUnverifiedOrders && (
                            <span className="text-red-500 ml-1">• {period.unverifiedOrderCount} doğrulanmamış</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-gray-800">{formatCurrency(period.totalEarnings)}</p>
                        {period.hasUnverifiedOrders && period.status !== 'Paid' && (
                          <p className="text-xs text-green-600">Ödenecek: {formatCurrency(period.verifiedEarnings)}</p>
                        )}
                        {(period.carryInAmount ?? 0) > 0 && period.status !== 'Paid' && (
                          <p className="text-xs text-sky-600">
                            Devirle toplam: {formatCurrency(period.payableAmount ?? 0)}
                          </p>
                        )}
                        <span
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: `${period.statusColor}20`,
                            color: period.statusColor,
                          }}
                        >
                          {period.statusDescription}
                        </span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="px-4 pb-4 bg-gray-50 border-t">
                    <div className="pt-3 space-y-3">
                      {/* Kazanç Detayı */}
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-2 bg-green-50 rounded-lg">
                          <p className="text-xs text-gray-600">✅ Doğrulanmış</p>
                          <p className="font-bold text-green-600">{formatCurrency(period.verifiedEarnings)}</p>
                          <p className="text-xs text-gray-500">{period.verifiedOrderCount} sipariş</p>
                        </div>
                        {period.unverifiedEarnings > 0 && (
                          <div className="p-2 bg-red-50 rounded-lg">
                            <p className="text-xs text-gray-600">⚠️ Doğrulanmamış</p>
                            <p className="font-bold text-red-500">{formatCurrency(period.unverifiedEarnings)}</p>
                            <p className="text-xs text-gray-500">{period.unverifiedOrderCount} sipariş</p>
                          </div>
                        )}
                      </div>

                      {/* Devir bilgisi — para kaybolmadı, ertelendi */}
                      <CarryOverNote period={period} minPayout={summary.minPayoutAmount} />

                      {period.status === 'PaymentPending' && (
                        <div className="text-sm text-amber-700 bg-amber-100 px-3 py-2 rounded-lg">
                          📅 Ödeme penceresi: {period.paymentWindowText}
                        </div>
                      )}
                      {period.status === 'Paid' && period.paidAtUtc && (
                        <div className="text-sm text-green-700 bg-green-100 px-3 py-2 rounded-lg">
                          ✓ {formatDate(period.paidAtUtc)} tarihinde ödendi
                          {period.transactionReference && ` • Ref: ${period.transactionReference}`}
                        </div>
                      )}
                      {period.hasUnverifiedOrders && period.status !== 'Paid' && (
                        <div className="text-xs text-red-700 bg-red-100 px-3 py-2 rounded-lg">
                          ⚠️ {period.unverifiedOrderCount} siparişiniz Amazon iade süresi dolduktan sonra (sipariş tarihi + ~45 gün) eklenti tarafından güncellenmediği için doğrulanmamış. 
                          Eklentinizi çalıştırarak güncelleyin.
                        </div>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPeriod(period);
                        }}
                        className="w-full py-2 text-sm font-medium text-orange-600 hover:text-orange-700 hover:bg-orange-100 rounded-lg transition-colors"
                      >
                        Detayları Görüntüle →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Period Detail Modal */}
      {selectedPeriod && (
        <PeriodDetailModal period={selectedPeriod} onClose={() => setSelectedPeriod(null)} />
      )}
    </div>
  );
};

export default EarningsTab;